import logger from '../lib/logger';
import React, { useState } from 'react';
import { BookOpen, GraduationCap, Clock, CheckCircle, Users, Heart, Loader2, AlertCircle, Book, Award, Bookmark } from 'lucide-react';
import { submitMadrassahForm } from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const LEVELS = [
  { name: "Level 1", description: "Beginner level for new students" },
  { name: "Level 2", description: "Intermediate level" },
  { name: "Level 3", description: "Advanced level" }
];

const SUBJECTS = [
  { name: "Aqaid", arabic: "عقاید", description: "Islamic Belief" },
  { name: "Fiqh", arabic: "فقه", description: "Islamic Laws" },
  { name: "Tarikh", arabic: "تاریخ", description: "Islamic History" },
  { name: "Akhlaq", arabic: "اخلاق", description: "Islamic Ethics" }
];

const MadrassahPage = () => {
  const [formData, setFormData] = useState({
    student_name: '',
    parent_name: '',
    email: '',
    phone: '',
    level: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      await submitMadrassahForm(formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ student_name: '', parent_name: '', email: '', phone: '', level: '' });
      }, 5000);
    } catch (err) {
      logger.error('Failed to submit registration:', err);
      setError('Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '50vh' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2f23] to-[#2a5a3d]" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="w-24 h-24 mx-auto mb-8 border-2 border-[#c5a572] flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-[#c5a572]" />
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6">Fatimia Madrassah</h1>
            <p className="text-white/70 text-lg">
              Islamic education program providing a safe environment for children to connect with Islamic resources and develop an affiliation with the Muslim community.
            </p>
            <div className="islamic-divider mt-8">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* Our Objective */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Our Objective</p>
                <h2 className="heading-2 mb-6">Building Strong Foundations</h2>
                <p className="text-[#5a6d62] text-lg leading-relaxed mb-6">
                  To provide a safe Islamic environment for our children to connect with Islamic resources and develop an affiliation with the Muslim community.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                      <Heart className="w-5 h-5 text-[#c5a572]" />
                    </div>
                    <span className="text-[#1a2f23]">Safe and nurturing Islamic environment</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-[#c5a572]" />
                    </div>
                    <span className="text-[#1a2f23]">Community connection and affiliation</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                      <Book className="w-5 h-5 text-[#c5a572]" />
                    </div>
                    <span className="text-[#1a2f23]">Access to comprehensive Islamic resources</span>
                  </div>
                </div>
              </div>
              <div className="card card-gold text-center py-12">
                <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                  <Users className="w-10 h-10 text-[#c5a572]" />
                </div>
                <h3 className="text-2xl text-[#1a2f23] mb-2">Administration</h3>
                <p className="text-[#5a6d62]">Fatimia Madrassah is directed and administered by dedicated volunteers from our community.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Focus - Quran */}
      <section className="section-dark py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '300px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Core Focus</p>
            <h2 className="text-4xl text-white mb-8">The Holy Quran</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Central to Madrassah's teachings is the learning of the Quran. Our dedicated instructors guide students through proper recitation and understanding.
            </p>
            <div className="quote-box bg-white/5 border-[#c5a572] backdrop-blur-sm">
              <p className="text-white/80 text-lg italic leading-relaxed">
                "Read! In the name of your Lord who created."
              </p>
              <p className="text-[#c5a572] mt-4 font-display text-lg">— Quran 96:1</p>
            </div>
            <div className="islamic-divider mt-12">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Our Courses</p>
            <h2 className="heading-2 mb-6">What We Teach</h2>
            <div className="islamic-divider">
              <div className="islamic-star" />
            </div>
          </div>

          {/* Quran Course */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="card card-gold">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 border-2 border-[#c5a572] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-8 h-8 text-[#c5a572]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl text-[#1a2f23] mb-2">Quran Recitation and Tajweed</h3>
                  <p className="text-[#c5a572] mb-4">Taught by Br. Hadi Askarzadeh</p>
                  <p className="text-[#5a6d62] mb-4">
                    Learn proper Quran recitation with correct pronunciation and Tajweed rules.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {LEVELS.map((level) => (
                      <span key={level.name} className="px-4 py-2 bg-[#c5a572]/10 text-[#8a7045] text-sm border border-[#c5a572]/30">
                        {level.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Islamic Studies */}
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 border-2 border-[#c5a572] flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-[#c5a572]" />
                </div>
                <div>
                  <h3 className="text-2xl text-[#1a2f23] mb-2">Islamic Studies</h3>
                  <p className="text-[#5a6d62]">
                    Classes cover the following topics on each grade level:
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {SUBJECTS.map((subject) => (
                  <div key={subject.name} className="text-center p-6 bg-[#f8f6f1] border border-[#c5a572]/20 hover:border-[#c5a572]/50 transition-colors">
                    <p className="text-2xl text-[#c5a572] mb-2 font-arabic">{subject.arabic}</p>
                    <h4 className="text-lg text-[#1a2f23] mb-1">{subject.name}</h4>
                    <p className="text-sm text-[#5a6d62]">{subject.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Books & Materials */}
      <section className="section section-alt">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-8 border-2 border-[#c5a572] flex items-center justify-center">
              <Bookmark className="w-10 h-10 text-[#c5a572]" />
            </div>
            <h2 className="heading-2 mb-6">Books & Materials</h2>
            <p className="text-[#5a6d62] text-lg leading-relaxed">
              All the books, materials, and stationery supplies will be provided by Madrassah.
            </p>
            <p className="text-[#c5a572] mt-4 text-sm uppercase tracking-wider">
              Costs covered by tuition fee
            </p>
          </div>
        </div>
      </section>

      {/* Levels & Registration */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid-2 items-start">
            {/* Levels Info */}
            <div>
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Class Levels</p>
              <h2 className="heading-2 mb-6">Our Programs</h2>
              <p className="text-[#5a6d62] mb-8 leading-relaxed">
                Students are placed in one of three levels based on their knowledge and experience. Each level offers age-appropriate instruction in Quran and Islamic studies.
              </p>

              <div className="space-y-6">
                {LEVELS.map((level, index) => (
                  <div key={level.name} className="card group">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center group-hover:bg-[#c5a572]/10 transition-colors">
                        <span className="text-2xl font-display text-[#c5a572]">{index + 1}</span>
                      </div>
                      <div>
                        <h4 className="text-lg text-[#1a2f23] group-hover:text-[#c5a572] transition-colors">{level.name}</h4>
                        <p className="text-sm text-[#5a6d62]">{level.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Registration Form */}
            <div className="card card-gold">
              <h3 className="text-2xl text-[#1a2f23] mb-2">Registration Form</h3>
              <p className="text-sm text-[#5a6d62] mb-6">Fill out the form to register your child for Madrassah.</p>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-[#c5a572]" />
                  </div>
                  <h3 className="text-2xl text-[#1a2f23] mb-2">Registration Received!</h3>
                  <p className="text-[#5a6d62]">We'll contact you shortly with next steps.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {error}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="studentName" className="text-sm uppercase tracking-wider text-[#5a6d62]">Student's Full Name *</Label>
                    <Input
                      id="studentName"
                      value={formData.student_name}
                      onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                      placeholder="Child's name"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="level" className="text-sm uppercase tracking-wider text-[#5a6d62]">Level *</Label>
                    <Select value={formData.level} onValueChange={(value) => setFormData({ ...formData, level: value })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((level) => (
                          <SelectItem key={level.name} value={level.name}>{level.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="parentName" className="text-sm uppercase tracking-wider text-[#5a6d62]">Parent/Guardian Name *</Label>
                    <Input
                      id="parentName"
                      value={formData.parent_name}
                      onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                      placeholder="Your name"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm uppercase tracking-wider text-[#5a6d62]">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm uppercase tracking-wider text-[#5a6d62]">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(604) 555-0123"
                      className="mt-2"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary mt-6" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Registration'
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MadrassahPage;
